import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, withModifiers as _withModifiers, withKeys as _withKeys } from "vue"

import { ref, computed, onMounted } from 'vue'
import moment from 'moment'
import { ASN1_TYPES, TIME_TYPES } from '@/utils/types'
import AutoComplete from '@/components/AutoComplete.vue'
import { useDisplay } from 'vuetify'
import { useTabsStore } from '@/stores/tabs'
import type { TreeNode } from '@/types/editor'


export default /*@__PURE__*/_defineComponent({
  __name: 'ElementMenu',
  props: {
    node: {},
    parent: {}
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const { mobile: isMobile } = useDisplay()
const store = useTabsStore()

const props = __props

const emit = __emit

// ─── State ────────────────────────────────────────────────────────────────────

const tag = ref<number | string | null>(props.node ? props.node.tag[0] : null)
const label = ref<string | null>(props.node ? props.node.label : null)
const length = ref<any>(props.node ? props.node.length[0] : null)
const content = ref<string | null>(props.node ? props.node.content[2] : null)
const pick = ref(false)
const date = ref(new Date())
const time = ref(new Date().toLocaleTimeString('en-GB'))
const oidCompletions = ref<string[]>([])

// ─── Computed ─────────────────────────────────────────────────────────────────

const types = ASN1_TYPES as Record<number | string, any>
const timeTypes = TIME_TYPES as string[]

const asn1TypesForSelect = computed(() => {
  return Object.entries(types).map(([key, value]) => ({
    title: value.name as string,
    value: key
  }))
})

const currentType = computed(() => {
  if (tag.value == null) return null
  return types[tag.value as number] ?? null
})

const hasCompletions = computed(() => {
  if (tag.value == null) return false
  if (Number(tag.value) === 6) return oidCompletions.value.length > 0
  return (currentType.value?.completions?.length ?? 0) > 0
})

const currentCompletions = computed<string[]>(() => {
  if (tag.value == null) return []
  if (Number(tag.value) === 6) return oidCompletions.value
  return currentType.value?.completions ?? []
})

const isTimeType = computed(() => {
  if (!currentType.value) return false
  return timeTypes.includes(currentType.value.name)
})

// ─── Lifecycle ────────────────────────────────────────────────────────────────

onMounted(() => {
  loadOidCompletions()
})

// ─── Methods ──────────────────────────────────────────────────────────────────

function loadOidCompletions(): void {
  try {
    if (store.state) {
      const oidsJson = store.state.get_all_oids()
      const oids = JSON.parse(oidsJson)
      if (Array.isArray(oids)) {
        oidCompletions.value = oids.map((oid: any) => {
          if (typeof oid === 'string') return oid
          if (typeof oid === 'object' && oid !== null) {
            if (oid.name && oid.oid) return `${oid.oid} - ${oid.name}`
            return oid.oid ?? String(oid)
          }
          return String(oid)
        })
      }
    }
  } catch (e) {
    console.warn('Could not load OID completions:', e)
    oidCompletions.value = []
  }
}

function translate(value: unknown): string {
  let bytes: Uint8Array
  if (typeof value === 'string') {
    bytes = new TextEncoder().encode(value)
  } else if (typeof value === 'number') {
    const buffer = new ArrayBuffer(4)
    const view = new DataView(buffer)
    view.setUint32(0, value, false)
    bytes = new Uint8Array(buffer)
  } else if (typeof value === 'object' && value !== null) {
    const jsonStr = JSON.stringify(value)
    bytes = new TextEncoder().encode(jsonStr)
  } else {
    throw new TypeError('Unsupported type for hex conversion')
  }
  const hex = Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
  return '0x' + hex
}

function confirmTime(): void {
  const d = new Date(date.value)
  const parts = time.value.split(':').map(Number)
  d.setHours(parts[0], parts[1], parts[2] ?? 0)
  const m = moment(d)
  const typeName = currentType.value?.name ?? ''

  if (typeName === 'TIME-OF-DAY') {
    content.value = m.format('HH:mm:ss')
  } else if (typeName === 'TIME') {
    content.value = m.format('YYYY-MM-DDTHH:mm:ss')
  } else if (typeName === 'DATE') {
    content.value = m.format('YYYY-MM-DD')
  } else if (typeName === 'DATE-TIME') {
    content.value = m.format('YYYY-MM-DDTHH:mm:ss')
  } else if (typeName === 'GeneralizedTime') {
    content.value = m.format('YYYYMMDDHHmmss')
  } else if (typeName === 'UTCTime') {
    content.value = m.format('YYMMDDHHmmssZ')
  } else if (typeName === 'DURATION') {
    const years = m.years()
    const months = m.months()
    const days = m.days()
    const hours = m.hours()
    const minutes = m.minutes()
    const seconds = m.seconds()

    let result = 'P'
    if (years) result += `${years}Y`
    if (months) result += `${months}M`
    if (days) result += `${days}D`
    if (hours || minutes || seconds) {
      result += 'T'
      if (hours) result += `${hours}H`
      if (minutes) result += `${minutes}M`
      if (seconds) result += `${seconds}S`
    }
    if (result === 'P') result = 'PT0S'

    content.value = result
  }
  pick.value = false
}

function verifyContent(): boolean {
  if (content.value === '' || content.value === null) return true
  if (tag.value === null) return false

  // For OIDs, extract just the OID part if user selected a formatted completion
  if (Number(tag.value) === 6 && content.value?.includes(' - ')) {
    content.value = content.value.split(' - ')[0].trim()
  }

  const typeEntry = types[tag.value as number]
  if (!typeEntry) return false

  if (typeEntry.rules(content.value)) return true

  if (typeEntry.transform) {
    const regex = typeEntry.transform.regex as RegExp
    if (regex.test(content.value!)) {
      content.value = typeEntry.transform.converter(content.value)
      return true
    }
  }

  if (!confirm('The content is not valid for the selected type. Do you still want to continue?')) {
    return false
  }

  content.value = translate(content.value)
  return true
}

function addNode(): void {
  if (!verifyContent()) return
  store.nodeAdded({
    tab: store.currentTab,
    parent: props.parent ?? 0,
    tag: tag.value,
    label: label.value,
    content: content.value ?? ''
  })
  emit('close')
}

function changeNode(): void {
  if (!verifyContent()) return
  if (!props.node) return

  store.nodeChanged({
    tab: store.currentTab,
    id: props.node.id,
    tag: tag.value,
    length: length.value !== props.node.length[0] ? length.value : null,
    content: content.value
  })

  if (props.node.label !== label.value) {
    store.nodeUpdated({
      tab: store.currentTab,
      id: props.node.id,
      value: label.value,
      field: 'label'
    })
  }
  emit('close')
}

return (_ctx: any,_cache: any) => {
  const _component_v_date_picker = _resolveComponent("v-date-picker")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_time_picker = _resolveComponent("v-time-picker")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_overlay = _resolveComponent("v-overlay")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createBlock(_component_v_card, {
    width: _unref(isMobile) ? '95vw' : '50vw',
    "max-height": _unref(isMobile) ? '90vh' : '70vh',
    class: "d-flex flex-column"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_overlay, {
        modelValue: pick.value,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((pick).value = $event)),
        class: "d-flex justify-center align-center",
        persistent: ""
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, {
            width: _unref(isMobile) ? '90vw' : 'auto'
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_text, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, {
                        cols: "12",
                        md: "auto"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_date_picker, {
                            modelValue: date.value,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((date).value = $event))
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_col, {
                        cols: "12",
                        md: "auto"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_time_picker, {
                            modelValue: time.value,
                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((time).value = $event)),
                            "use-seconds": true
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    onClick: confirmTime,
                    color: "primary",
                    variant: "tonal"
                  }, {
                    default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                      _createTextVNode("Confirm", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (pick.value = false)),
                    variant: "tonal"
                  }, {
                    default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                      _createTextVNode("Cancel", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["width"])
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(_component_v_card_title, { class: "text-center flex-0" }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(props.node ? 'Edit Node' : 'Add Node'), 1)
        ]),
        _: 1
      }),
      _createVNode(_component_v_divider),
      _createVNode(_component_v_card_text, {
        class: "flex-grow-1",
        style: {"overflow-y":"auto"}
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_form, {
            onKeydown: _cache[10] || (_cache[10] = _withKeys(_withModifiers(($event: any) => (props.node ? changeNode() : addNode()), ["prevent"]), ["enter"]))
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_select, {
                modelValue: tag.value,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((tag).value = $event)),
                label: "ASN.1 TYPE",
                items: asn1TypesForSelect.value,
                "item-title": "title",
                "item-value": "value",
                density: "compact"
              }, null, 8, ["modelValue", "items"]),
              _createVNode(_component_v_text_field, {
                modelValue: label.value,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((label).value = $event)),
                label: "Label",
                placeholder: "Label",
                density: "compact"
              }, null, 8, ["modelValue"]),
              (props.node != null)
                ? (_openBlock(), _createBlock(_component_v_text_field, {
                    key: 0,
                    modelValue: length.value,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((length).value = $event)),
                    label: "Length",
                    placeholder: "Length",
                    density: "compact"
                  }, null, 8, ["modelValue"]))
                : _createCommentVNode("", true),
              (tag.value != null && tag.value != 48 && tag.value != 49)
                ? (_openBlock(), _createBlock(_component_v_row, {
                    key: 1,
                    class: "align-center"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, null, {
                        default: _withCtx(() => [
                          (!hasCompletions.value)
                            ? (_openBlock(), _createBlock(_component_v_text_field, {
                                key: 0,
                                modelValue: content.value,
                                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((content).value = $event)),
                                label: "Content",
                                placeholder: currentType.value?.example ?? '',
                                density: "compact",
                                "hide-details": ""
                              }, null, 8, ["modelValue", "placeholder"]))
                            : (_openBlock(), _createBlock(AutoComplete, {
                                key: 1,
                                modelValue: content.value,
                                "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((content).value = $event)),
                                label: "Content",
                                completions: currentCompletions.value
                              }, null, 8, ["modelValue", "completions"]))
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_col, {
                        cols: "auto",
                        class: "d-flex"
                      }, {
                        default: _withCtx(() => [
                          (isTimeType.value)
                            ? (_openBlock(), _createBlock(_component_v_btn, {
                                key: 0,
                                icon: "mdi-calendar",
                                onClick: _cache[9] || (_cache[9] = ($event: any) => (pick.value = true)),
                                variant: "text",
                                size: "small"
                              }))
                            : _createCommentVNode("", true),
                          _createVNode(_component_v_btn, {
                            icon: "",
                            variant: "text",
                            size: "small"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_icon, null, {
                                default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                                  _createTextVNode("mdi-help-circle", -1)
                                ]))]),
                                _: 1
                              }),
                              _createVNode(_component_v_tooltip, {
                                activator: "parent",
                                location: "bottom"
                              }, {
                                default: _withCtx(() => [
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList((currentType.value?.description ?? '').split('\n'), (chunk, index) => {
                                    return (_openBlock(), _createElementBlock("span", { key: index }, [
                                      _createTextVNode(_toDisplayString(chunk), 1),
                                      _cache[17] || (_cache[17] = _createElementVNode("br", null, null, -1))
                                    ]))
                                  }), 128))
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_divider),
      _createVNode(_component_v_card_actions, { class: "pa-4 flex-0" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_spacer),
          (!props.node)
            ? (_openBlock(), _createBlock(_component_v_btn, {
                key: 0,
                onClick: _cache[11] || (_cache[11] = ($event: any) => (addNode())),
                color: "primary",
                variant: "tonal"
              }, {
                default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                  _createTextVNode("Add", -1)
                ]))]),
                _: 1
              }))
            : (_openBlock(), _createBlock(_component_v_btn, {
                key: 1,
                onClick: _cache[12] || (_cache[12] = ($event: any) => (changeNode())),
                color: "primary",
                variant: "tonal"
              }, {
                default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                  _createTextVNode("Apply Changes", -1)
                ]))]),
                _: 1
              })),
          _createVNode(_component_v_btn, {
            onClick: _cache[13] || (_cache[13] = ($event: any) => (emit('close'))),
            variant: "tonal"
          }, {
            default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
              _createTextVNode("Close", -1)
            ]))]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["width", "max-height"]))
}
}

})